import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Loads Google Analytics 4 exactly once (on first mount) and sends a `page_view`
 * event every time the React Router pathname changes.
 *
 * Zero effect when `REACT_APP_GA_MEASUREMENT_ID` is not set — so the app runs
 * clean in development without polluting your live dashboard with dev traffic.
 *
 * Provide the ID via `frontend/.env`:
 *   REACT_APP_GA_MEASUREMENT_ID=G-XXXXXXXXXX
 */
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

function injectGaScript(gaId) {
  if (document.getElementById("ga4-loader")) return; // already loaded
  const s = document.createElement("script");
  s.id = "ga4-loader";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  // send_page_view=false because we'll send it manually on route changes
  // (React Router doesn't trigger a real page load, so GA's auto page_view misses SPA nav).
  window.gtag("config", gaId, { send_page_view: false });
}

function trackPageView(gaId, pathname, search) {
  if (!window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: pathname + search,
    page_location: window.location.href,
    page_title: document.title,
    send_to: gaId,
  });
}

export default function useGoogleAnalytics() {
  const location = useLocation();
  const initialized = useRef(false);
  const lastTrackedPath = useRef("");

  useEffect(() => {
    if (!GA_ID || !GA_ID.startsWith("G-")) return;
    if (!initialized.current) {
      injectGaScript(GA_ID);
      initialized.current = true;
    }
    // De-duplicate: React Strict Mode fires effects twice in dev; a legitimate
    // navigation always changes pathname+search, so equality is a safe guard.
    const key = location.pathname + location.search;
    if (lastTrackedPath.current === key) return;
    lastTrackedPath.current = key;
    trackPageView(GA_ID, location.pathname, location.search);
  }, [location.pathname, location.search]);
}
